"use client";

import { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams, Link } from "react-router-dom";
import {
  StarIcon,
  PhoneIcon,
  EnvelopeIcon,
  MapPinIcon,
  BriefcaseIcon,
  GlobeAltIcon,
  BuildingOffice2Icon,
  UsersIcon,
  ExclamationTriangleIcon,
  ChevronRightIcon,
  CurrencyRupeeIcon,
  ClockIcon,
} from "@heroicons/react/24/outline";
import {
  Button,
  Box,
  Text,
  Card,
  Modal,
  ModalBody,
  ModalContent,
  ModalCloseButton,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Textarea,
  Input,
  VStack,
  useDisclosure,
  Select,
  Skeleton,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  useToast,
  GridItem,
  Flex,
  Image,
  Badge,
} from "@chakra-ui/react";
import { FaPlus } from "react-icons/fa";
import { TimeDifference } from "../common/TimeDifference";
import {
  fetchCompanyDetailsAsync,
  selectCompanyDetails,
} from "./CompanySingleSlice/CompanySingleSlice";
import {
  fetchJobListByCompanyAsync,
  selectCompanyJobs,
} from "./JobsByCompanySlice/JobsByCompanySlice";
import {
  fetchCityListAsync,
  selectCity,
} from "../AdminPanel/CityList/CityListSlice";
import {
  fetchPublicJobTitlesAsync,
  selectJobTitles,
} from "../AdminPanel/JobPostingSlice/JobTitles/JobTitlesSlice";
import { addCompanyReviewAsync } from "../employerDashboard/employerDashboardSlice/employerCompanyReviews/CompanyReviewsSlice";
import {
  fetchPublicCompanyReviewsAsync,
  selectPublicCompanyReviews,
} from "../Public/PublicCompanyReviews/PublicCompanyReviewsSlice";
import { EMPLOYER_COMPANY_LOGO_SAVE_URL, EMPLOYER_COMPANY_MEDIA_URL } from "../common/URLs";
import companylogo from "./logo1png.png"
// Loading Skeleton Components
const CompanyProfileSkeleton = () => (
  <div className="min-h-screen bg-white animate-pulse">
    <div className="h-[40vh] border-b bg-gradient-to-r from-gray-100 to-gray-200">
      <div className="max-w-7xl mx-auto px-6 py-8 flex flex-col md:flex-row items-center gap-8">
        <Skeleton height="100px" width="100px" borderRadius="lg" />
        <VStack align="start" spacing={4} flex={1}>
          <Skeleton height="36px" width="300px" />
          <Skeleton height="24px" width="200px" />
          <Skeleton height="20px" width="400px" />
        </VStack>
      </div>
    </div>
    <div className="max-w-7xl mx-auto px-6 py-8">
      <div className="grid md:grid-cols-3 gap-8">
        <div className="md:col-span-2">
          <Skeleton height="200px" mb={4} />
          <Skeleton height="150px" mb={4} />
          <Skeleton height="150px" />
        </div>
        <Skeleton height="500px" />
      </div>
    </div>
  </div>
);

const JobCardSkeleton = () => (
  <div className="border border-gray-200 rounded-lg p-4 animate-pulse">
    <div className="flex items-center space-x-4 mb-4">
      <div className="w-12 h-12 bg-gray-200 rounded-full" />
      <div className="flex-1">
        <div className="h-5 bg-gray-200 rounded w-3/4 mb-2" />
        <div className="h-4 bg-gray-200 rounded w-1/2" />
      </div>
    </div>
    <div className="space-y-3">
      <div className="h-4 bg-gray-200 rounded w-full" />
      <div className="h-4 bg-gray-200 rounded w-5/6" />
    </div>
  </div>
);

// Empty State Component
const EmptyState = ({ message, icon: Icon }) => (
  <div className="flex flex-col items-center justify-center p-8 text-center bg-white rounded-lg shadow-sm">
    <div className="w-16 h-16 mb-4 text-gray-400">{Icon}</div>
    <Text color="gray.500" fontSize="lg" fontWeight="medium">
      {message}
    </Text>
  </div>
);

// Error State Component
const ErrorState = ({ message }) => (
  <Alert
    status="error"
    variant="subtle"
    flexDirection="column"
    alignItems="center"
    justifyContent="center"
    textAlign="center"
    height="200px"
    borderRadius="lg"
    className="my-4"
  >
    <AlertIcon boxSize="40px" mr={0} />
    <AlertTitle mt={4} mb={1} fontSize="lg">
      Error Loading Data
    </AlertTitle>
    <AlertDescription maxWidth="sm">{message}</AlertDescription>
  </Alert>
);

const CompanyOverview = ({
  companyProfile,
  companyJobs,
  reviews,
  handleOpenReviewModal,
  isLoading,
  error,
}) => {
  const base_url = "https://api.indiahirings.com/uploads/company/";

  if (isLoading) return <CompanyProfileSkeleton />;
  if (error) return <ErrorState message={error} />;
  if (!companyProfile)
    return (
      <EmptyState
        message="Company profile not found"
        icon={<ExclamationTriangleIcon className="w-full h-full" />}
      />
    );

  return (
    <div className="min-h-screen bg-white">
      <div className="h-auto md:h-[40vh] border-b bg-gradient-to-r from-blue-50 to-cyan-50">
        <div className="max-w-7xl mx-auto px-6 py-8 flex flex-col md:flex-row items-center gap-8">
          <div className="w-32 h-32 rounded-xl overflow-hidden border-2 border-white ">
            <img
              // src={`${base_url}${companyProfile.company_logo}`}
              src={companyProfile.company_logo ? `${base_url}${companyProfile.company_logo}` : companylogo}

              // alt={companyProfile.company_name}
              className="w-full h-full object-contain"
            />
          </div>
          <div className="flex-1 text-center md:text-left">
            <h1 className="text-4xl text-start font-bold text-gray-900 mb-2">
              {companyProfile.company_name}
            </h1>
            <div className="flex items-center gap-2 justify-center md:justify-start mb-4">
              <StarIcon className="w-6 h-6 text-yellow-400 fill-current" />
              <span className="text-lg font-semibold">
                {companyProfile.average_rating || "4.5"}/5
              </span>
              <span className="text-gray-500">
                ({reviews?.length || 0} reviews)
              </span>
            </div>
            <div className="flex flex-wrap gap-4 justify-center md:justify-start text-gray-600">
              <div className="flex items-center gap-2">
                <BuildingOffice2Icon className="w-5 h-5" />
                <span className="capitalize">{companyProfile.industry_type || "Technology"}</span>
              </div>
              <div className="flex items-center gap-2">
                <MapPinIcon className="w-5 h-5" />
                <span>
                  {companyProfile.city?.name || "City"},{" "}
                  {companyProfile.state?.name || "Country"},{" "}
                  {companyProfile.country?.name || "Country"}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <UsersIcon className="w-5 h-5" />
                <span>
                  {companyProfile.number_of_employee || "1000+"} employees
                </span>
              </div>
            </div>
          </div>
          <div className="flex gap-4">
            {/* <Button
              leftIcon={<FaPlus />}
              colorScheme="blue"
              variant="outline"
              size="md"
            // className="shadow-md"
            >
              Follow
            </Button> */}
            <Button
              colorScheme="teal"
              variant="outline"
              size="md"
              // className="shadow-md"
              onClick={handleOpenReviewModal}
            >
              Write Review
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto  py-8">
        <div className="grid md:grid-cols-3 gap-8">
          <div className="md:col-span-2">
            <Tabs variant="soft-rounded" colorScheme="blue">
              <TabList className="bg-gray-100 p-1 rounded-lg mb-6">
                <Tab className="rounded-md px-4 py-2">Overview</Tab>
                <Tab className="rounded-md px-4 py-2">Interview Process</Tab>
                <Tab className="rounded-md px-4 py-2">Jobs</Tab>
                <Tab className="rounded-md px-4 py-2">Media</Tab>
              </TabList>
              <TabPanels>
                <TabPanel>
                  <CompanyDetails companyProfile={companyProfile} />
                </TabPanel>
                <TabPanel>
                  <InterviewProcess companyProfile={companyProfile} />
                </TabPanel>
                <TabPanel>
                  <JobsList jobs={companyJobs} isLoading={isLoading} />
                </TabPanel>
                <TabPanel>
                  <CompanyMedia companyProfile={companyProfile} />
                </TabPanel>
              </TabPanels>
            </Tabs>
          </div>
          <div>
            <ReviewsSidebar reviews={reviews} isLoading={isLoading} />
          </div>
        </div>
      </div>
    </div>
  );
};

const CompanyDetails = ({ companyProfile }) => (
  <div className="space-y-6">
    <Card className="p-6">
      <h2 className="text-2xl font-bold mb-4">About Us</h2>
      <Text className="text-gray-600 leading-relaxed">
        {companyProfile.company_description ||
          "Company description not available"}
      </Text>
    </Card>

    <Card className="p-6">
      <h2 className="text-2xl font-bold mb-4">Contact Information</h2>
      <div className="space-y-4">
        <div className="flex items-center gap-3">
          <MapPinIcon className="w-6 h-6 text-blue-600" />
          <span className="text-gray-600">
            {companyProfile.address1},{companyProfile.city?.name},
            {companyProfile.country?.name}
          </span>
        </div>
        <div className="flex items-center gap-3">
          <PhoneIcon className="w-6 h-6 text-blue-600" />
          <span className="text-gray-600">{companyProfile.phone}</span>
        </div>
        <div className="flex items-center gap-3">
          <EnvelopeIcon className="w-6 h-6 text-blue-600" />
          <span className="text-gray-600">{companyProfile.email}</span>
        </div>
        <div className="flex items-center gap-3">
          <GlobeAltIcon className="w-6 h-6 text-blue-600" />
          <a
            href={companyProfile.url}
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-600 hover:underline"
          >
            {companyProfile.url}
          </a>
        </div>
      </div>
    </Card>
  </div>
);

const InterviewProcess = ({ companyProfile }) => {
  const steps = companyProfile.company_interview_process || [
    { title: "Initial Screening", description: "Phone or video call with HR" },
    {
      title: "Technical Assessment",
      description: "Coding challenge or technical test",
    },
    {
      title: "Technical Interview",
      description: "Deep dive into technical skills",
    },
    {
      title: "Culture Fit",
      description: "Meet the team and discuss company culture",
    },
  ];

  return (
    <div className="space-y-6">
      <Card className="p-6">
        <h2 className="text-2xl font-bold mb-6">Interview Process</h2>
        <div className="space-y-8">
          {steps.map((step, index) => (
            <div key={index} className="flex gap-4">
              <div className="flex flex-col items-center">
                <div className="w-8 h-8 rounded-full bg-blue-600 text-white flex items-center justify-center font-bold">
                  {index + 1}
                </div>
                {index < steps.length - 1 && (
                  <div className="w-0.5 h-full bg-blue-200 mt-2" />
                )}
              </div>
              <div>
                <h3 className="text-lg font-semibold">{step.title}</h3>
                <p className="text-gray-600 mt-1">{step.description}</p>
              </div>
            </div>
          ))}
        </div>
      </Card>

      <Card className="p-6">
        <h2 className="text-2xl font-bold mb-4">Interview Tips</h2>
        <ul className="space-y-3">
          <li className="flex items-center gap-2">
            <ChevronRightIcon className="w-5 h-5 text-green-500" />
            <span>Research the company thoroughly</span>
          </li>
          <li className="flex items-center gap-2">
            <ChevronRightIcon className="w-5 h-5 text-green-500" />
            <span>Prepare relevant technical examples</span>
          </li>
          <li className="flex items-center gap-2">
            <ChevronRightIcon className="w-5 h-5 text-green-500" />
            <span>Practice common interview questions</span>
          </li>
          <li className="flex items-center gap-2">
            <ChevronRightIcon className="w-5 h-5 text-green-500" />
            <span>Have questions ready for the interviewer</span>
          </li>
        </ul>
      </Card>
    </div>
  );
};

const JobsList = ({ jobs, isLoading }) => {
  if (isLoading) {
    return (
      <div className="space-y-4">
        {[1, 2, 3].map((i) => (
          <JobCardSkeleton key={i} />
        ))}
      </div>
    );
  }

  if (!jobs?.length) {
    return (
      <EmptyState
        message="No open positions at the moment"
        icon={<BriefcaseIcon className="w-full h-full" />}
      />
    );
  }

  return (
    <div className="space-y-4">
      {jobs.map((job) => (
        <GridItem key={job.uuid}>
          <Box
            borderWidth={1}
            borderRadius="lg"
            bg="white"
            // boxShadow="md"
            _hover={{ transform: "translateY(-2px)" }}
            transition="all 0.2s"
            overflow="hidden"
          >
            <Flex p={6}>
              <Box flexShrink={0} mr={4}>
                {/* <Image
                  // src={`${EMPLOYER_COMPANY_LOGO_SAVE_URL}${job.company_logo}`}
                  src={job.company_logo ? `${EMPLOYER_COMPANY_LOGO_SAVE_URL}${job.company_logo}` : companylogo}
                  name={job.name}
                  className="h-16 w-16 object-cover rounded-full"
                  // alt={company.company_name || "Company Logo"}
                  // className="h-16 w-auto object-contain"
                  onError={(e) => (e.target.src = companylogo)}
                /> */}
              </Box>
              <Box flex={1}>
                <Link to={`/jobs/${job.uuid}`}>
                  <div
                    fontSize="xl"
                    fontWeight="bold"
                    mb={1}
                    className="text-black font-semibold text-xl flex-wrap flex items-center "
                  >
                    {job?.job_title?.title
                      ? job?.job_title?.title
                      : job.job_name}


                    <Badge colorScheme="blue" className="ml-2">{job.job_tag}</Badge>


                  </div>
                  <Text color="gray.600" fontSize="sm" mb={1}>
                    <span className="text-blue-600 font-medium">
                      <strong>{job?.job_name || "Job Name"}</strong>
                    </span>
                    <span className="flex items-center gap-1"> <BuildingOffice2Icon className="w-4 h-4  text-zinc-500" />{job?.department?.title || "Job Department Name"}
                    </span>
                  </Text>

                  <Text color="gray.600" fontSize="sm" mb={1}>
                    <div className=" text-left font-normal text-gray-500 ">
                      <span className="flex items-center gap-1">
                        <BriefcaseIcon className="w-4 h-4  text-zinc-500" />
                        {job.work_location_type || "Job Type"}
                      </span>
                      <div className="flex items-center gap-1" > <CurrencyRupeeIcon className="w-4 h-4 text-zinc-500" />
                        {job.salary_min_amount === null ||
                          job.salary_min_amount === 0
                          ? "Not Disclosed"
                          : `₹${job.salary_min_amount || "N/A"} - ₹${job.salary_max_amount || "N/A"
                          } / ${job.salary_per_type || "Not Specified"}`}
                      </div>


                    </div>
                  </Text>
                  <Text color="gray.600" fontSize="sm" mb={1} className="flex flex-wrap gap-1">
                    <span className="flex items-center gap-1">
                      <MapPinIcon className="w-4 h-4  text-zinc-500" />
                      {job?.job_locations
                        .map((job) => job?.job_location?.title)
                        .filter(Boolean) // Removes undefined or null values
                        .join(", ")}
                    </span>
                  </Text>

                  <Text color="gray.600" fontSize="sm" mb={1} className="flex items-center gap-1">
                    <ClockIcon className="w-4 h-4  text-zinc-500" />
                    <TimeDifference dateTimeString={job.created_at} />
                  </Text>


                </Link>
              </Box>

              {/* Bookmark Icon */}
              {/* <div className="flex flex-col items-end justify-between">
                <button
                  onClick={() => handleBookmarkClick(job.uuid)}
                  className="bookmark"
                >
                  {bookmarkedJobs[job.uuid] ? (
                    <svg
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="black"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M6 3C4.9 3 4 3.9 4 5V21L12 16.5L20 21V5C20 3.9 19.1 3 18 3H6Z"
                        stroke="black"
                        strokeWidth="2"
                      />
                    </svg>
                  ) : (
                    <svg
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M6 3C4.9 3 4 3.9 4 5V21L12 16.5L20 21V5C20 3.9 19.1 3 18 3H6Z"
                        stroke="black"
                        strokeWidth="2"
                      />
                    </svg>
                  )}
                </button>
                {appliedJobs[job.uuid] && (
                  <span className="text-green-600 text-sm font-semibold flex items-center gap-1">
                    ✅ Applied
                  </span>
                )}
              </div> */}
            </Flex>
          </Box>
        </GridItem>
      ))}
    </div>
  );
};

const CompanyMedia = ({ companyProfile }) => {
  return (
    <div>
      {companyProfile && companyProfile.company_media?.length > 0 && (
        <div className=" rounded-lg">
          <h3 className="text-3xl font-bold text-center mb-6 text-gray-800">
            Gallery
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {(() => {
              const videoItem = companyProfile.company_media.find(
                (item) => item.type === "video"
              );
              return videoItem ? (
                <div className="md:col-span-2 md:row-span-2 rounded-lg overflow-hidden h-[400px] shadow-lg">
                  <video
                    controls
                    autoPlay
                    className="w-full h-full object-cover"
                  >
                    <source
                      src={`${EMPLOYER_COMPANY_MEDIA_URL}/${videoItem.file}`}
                      type="video/mp4"
                    />
                    Your browser does not support the video tag.
                  </video>
                </div>
              ) : null;
            })()}

            {companyProfile.company_media
              ?.filter((item) => item.type === "image")
              .slice(0, 5)
              .map((item, index) => (
                <div
                  key={index}
                  className="rounded-lg overflow-hidden shadow-md h-[200px]"
                >
                  <img
                    src={`${EMPLOYER_COMPANY_MEDIA_URL}/${item.file}`}
                    alt={`Gallery Image ${index + 1}`}
                    className="w-full h-full object-cover"
                  />
                </div>
              ))}
          </div>
        </div>
      )}
    </div>
  );
};

const ReviewsSidebar = ({ reviews, isLoading }) => {
  if (isLoading) {
    return (
      <div className="space-y-4">
        {[1, 2, 3].map((i) => (
          <Skeleton key={i} height="150px" rounded="lg" />
        ))}
      </div>
    );
  }

  if (!reviews?.length) {
    return (
      <EmptyState
        message="No reviews yet"
        icon={<StarIcon className="w-full h-full" />}
      />
    );
  }

  return (
    <div className="space-y-4">
      <h2 className="text-2xl font-bold">Recent Reviews</h2>
      {reviews.map((review) => (
        <Card key={review.uuid} className="p-4">
          <div className="flex items-center gap-2 mb-2">
            <StarIcon className="w-5 h-5 text-yellow-400 fill-current" />
            <span className="font-semibold">{review.review_rating}/5</span>
          </div>
          <h3 className="font-semibold text-lg">{review.review_title}</h3>
          <p className="text-gray-600 mt-2 text-sm line-clamp-3">
            {review.review_description}
          </p>
          <div className="mt-4 text-sm text-gray-500">
            By {review.candidate?.name} • {review.job_title?.title}
          </div>
        </Card>
      ))}
    </div>
  );
};

export function CompanySingle2() {
  const dispatch = useDispatch();
  const { uuid } = useParams();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const toast = useToast();

  const cities = useSelector(selectCity);
  const jobTitles = useSelector(selectJobTitles);

  const companyProfile = useSelector(selectCompanyDetails);
  const companyJobs = useSelector(selectCompanyJobs);
  const reviews = useSelector(selectPublicCompanyReviews);

  const { isOpen, onOpen, onClose } = useDisclosure();
  const [reviewData, setReviewData] = useState({
    title: "",
    description: "",
    rating: 5,
    jobTitleUuid: "",
    jobLocationUuid: "",
  });

  const handleReviewSubmit = async () => {
    try {
      const { title, description, rating, jobTitleUuid, jobLocationUuid } =
        reviewData;

      if (!title || !description || !jobTitleUuid || !jobLocationUuid) {
        toast({
          title: "Missing Fields",
          description: "Please fill in all required fields",
          status: "error",
          duration: 3000,
        });
        return;
      }

      await dispatch(
        addCompanyReviewAsync({
          company_uuid: uuid,
          job_title_uuid: jobTitleUuid,
          job_location_uuid: jobLocationUuid,
          review_title: title,
          review_rating: rating,
          review_description: description,
        })
      );

      toast({
        title: "Review Submitted",
        description: "Thank you for your feedback!",
        status: "success",
        duration: 3000,
      });

      onClose();
      setReviewData({
        title: "",
        description: "",
        rating: 5,
        jobTitleUuid: "",
        jobLocationUuid: "",
      });

      dispatch(fetchPublicCompanyReviewsAsync({ uuid }));
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to submit review. Please try again.",
        status: "error",
        duration: 3000,
      });
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      setError(null);
      try {
        if (uuid) {
          await Promise.all([
            dispatch(fetchCompanyDetailsAsync({ uuid })),
            dispatch(fetchJobListByCompanyAsync({ uuid })),
            dispatch(fetchPublicCompanyReviewsAsync({ uuid })),
            dispatch(fetchPublicJobTitlesAsync()),
            dispatch(fetchCityListAsync()),
          ]);
        }
      } catch (error) {
        setError("Failed to load company data. Please try again later.");
        console.error("Error fetching data:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
    window.scrollTo(0, 0);
  }, [uuid, dispatch]);

  return (
    <Box minHeight="100vh" className="bg-gray-50">
      <CompanyOverview
        companyProfile={companyProfile}
        companyJobs={companyJobs}
        reviews={reviews}
        handleOpenReviewModal={onOpen}
        isLoading={isLoading}
        error={error}
      />

      <Modal isOpen={isOpen} onClose={onClose} size="xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader className="bg-blue-600 text-white">
            Write a Review
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody className="p-6">
            <VStack spacing={4}>
              <Select
                placeholder="Select Job Title"
                value={reviewData.jobTitleUuid}
                onChange={(e) =>
                  setReviewData({
                    ...reviewData,
                    jobTitleUuid: e.target.value,
                  })
                }
              >
                {jobTitles && jobTitles.map((job) => (
                  <option key={job.uuid} value={job.uuid}>
                    {job.title}
                  </option>
                ))}
              </Select>

              <Select
                placeholder="Select Location"
                value={reviewData.jobLocationUuid}
                onChange={(e) =>
                  setReviewData({
                    ...reviewData,
                    jobLocationUuid: e.target.value,
                  })
                }
              >
                {cities && cities.map((city) => (
                  <option key={city.uuid} value={city.uuid}>
                    {city.title}
                  </option>
                ))}
              </Select>

              <Input
                placeholder="Review Title"
                value={reviewData.title}
                onChange={(e) =>
                  setReviewData({
                    ...reviewData,
                    title: e.target.value,
                  })
                }
              />

              <Textarea
                placeholder="Write your review..."
                value={reviewData.description}
                onChange={(e) =>
                  setReviewData({
                    ...reviewData,
                    description: e.target.value,
                  })
                }
                rows={5}
              />

              <div className="w-full">
                <Text mb={2}>Rating</Text>
                <div className="flex gap-2">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <button
                      key={star}
                      onClick={() =>
                        setReviewData({
                          ...reviewData,
                          rating: star,
                        })
                      }
                      className={`p-2 rounded-full ${star <= reviewData.rating
                        ? "text-yellow-400"
                        : "text-gray-300"
                        }`}
                    >
                      <StarIcon className="w-8 h-8 fill-current" />
                    </button>
                  ))}
                </div>
              </div>
            </VStack>
          </ModalBody>
          <ModalFooter className="space-x-4">
            <Button variant="ghost" onClick={onClose}>
              Cancel
            </Button>
            <Button colorScheme="blue" onClick={handleReviewSubmit}>
              Submit Review
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
}